import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { motion } from "framer-motion";
import {
  User,
  Mail,
  Phone,
  CheckCircle,
  AlertCircle,
  Loader2,
  // MessageSquare, // Commented out for Facebook review
  Zap,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { MoujibLogo } from "../components/ui/logo";
import { useApi, API_ENDPOINTS } from "../lib/api";

const SignupPage: React.FC = () => {
  const { user, isAuthenticated, isLoading, logout } = useAuth0();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const api = useApi();

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [step, setStep] = useState<"form" | "code">("form");
  const [verificationCode, setVerificationCode] = useState("");
  const [codeStatus, setCodeStatus] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Get redirect path from URL params. A user who just finished signup has no
  // connected page yet, so /accounts is the right default — and unlike the old
  // /dashboard default it is a route that still exists (that one fell through
  // the catch-all and dumped people on the marketing landing page).
  const redirectTo = searchParams.get("redirect") || "/accounts";

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/login");
      return;
    }

    // Pre-fill form with Facebook data if available
    if (user) {
      setFormData((prev) => ({
        ...prev,
        name: user.name || "",
        email: user.email || "",
      }));
    }
  }, [isLoading, isAuthenticated, user, navigate]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Full name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Phone validation
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    const cleanPhone = formData.phone.replace(/[-\s\(\)]/g, "");
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!phoneRegex.test(cleanPhone)) {
      newErrors.phone = "Please enter a valid phone number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (step === "form") {
      if (!validateForm()) {
        return;
      }

      setIsSubmitting(true);
      setSubmitError(null);
      setCodeStatus(null);

      try {
        const response = await api.post(API_ENDPOINTS.AUTH_SEND_EMAIL_CODE, {
          email: formData.email.trim().toLowerCase(),
        });
        if (response.data.success) {
          setStep("code");
          setResendCooldown(60);
        } else {
          setSubmitError(
            response.data.error || "Failed to send verification code"
          );
        }
      } catch (error: any) {
        if (error.response?.status === 429) {
          setSubmitError("Please wait before requesting another code.");
        } else if (error.response?.status === 400) {
          setSubmitError(error.response.data.error || "Invalid email");
        } else {
          setSubmitError("Failed to send verification code. Please try again.");
        }
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (step === "code") {
      if (!verificationCode.trim() || verificationCode.trim().length < 6) {
        setCodeStatus("Please enter the 6-digit code sent to your email");
        return;
      }

      setIsSubmitting(true);
      setSubmitError(null);
      setCodeStatus(null);

      try {
        const verifyRes = await api.post(API_ENDPOINTS.AUTH_VERIFY_EMAIL_CODE, {
          email: formData.email.trim().toLowerCase(),
          code: verificationCode.trim(),
        });
        if (verifyRes.data.success) {
          const completeRes = await api.post(
            API_ENDPOINTS.AUTH_COMPLETE_SIGNUP,
            {
              name: formData.name.trim(),
              email: formData.email.trim().toLowerCase(),
              phone: formData.phone.trim(),
            }
          );
          if (completeRes.data.success) {
            navigate(redirectTo);
          } else {
            setSubmitError(completeRes.data.error || "Signup failed");
          }
        } else {
          setCodeStatus("Invalid verification code");
        }
      } catch (error: any) {
        if (error.response?.status === 400) {
          setCodeStatus(error.response.data.error || "Invalid or expired code");
        } else if (error.response?.status === 429) {
          setCodeStatus("Too many attempts. Please request a new code.");
        } else {
          setCodeStatus("Verification failed. Please try again.");
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(
      () => setResendCooldown((s) => (s > 0 ? s - 1 : 0)),
      1000
    );
    return () => clearInterval(t);
  }, [resendCooldown]);

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    setIsSubmitting(true);
    setSubmitError(null);
    setCodeStatus(null);
    try {
      const response = await api.post(API_ENDPOINTS.AUTH_SEND_EMAIL_CODE, {
        email: formData.email.trim().toLowerCase(),
      });
      if (response.data.success) {
        setResendCooldown(60);
        setCodeStatus("A new code has been sent.");
      } else {
        setSubmitError(response.data.error || "Failed to resend code");
      }
    } catch (error: any) {
      setSubmitError("Failed to resend code");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin + "/login",
      },
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="relative w-full max-w-md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Card className="shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
            <CardHeader className="text-center pb-8 pt-12">
              {/* Logo and Branding */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 100 }}
                className="mx-auto mb-6"
              >
                <MoujibLogo size="xxl" variant="color" />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <CardTitle className="text-3xl font-bold text-gray-900 mb-2">
                  Complete Setup
                </CardTitle>
                <p className="text-gray-600 text-lg mb-6">
                  Welcome to Moujib! Just a few details to get started.
                </p>

                {user && (
                  <div className="flex items-center justify-center space-x-2 text-sm bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-green-700 font-medium">
                      Connected as {user.name}
                    </span>
                  </div>
                )}

                {/* Features Preview */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="mb-6 grid grid-cols-1 gap-3"
                >
                  {/* COMMENTED OUT FOR FACEBOOK REVIEW - Only submitting comment moderation */}
                  {/* <div className="flex items-center space-x-3 text-gray-700 bg-blue-50 rounded-lg p-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <MessageSquare className="h-4 w-4 text-blue-600" />
                    </div>
                    <span className="text-sm">
                      Auto-reply in Derja, Arabic & French
                    </span>
                  </div> */}
                  <div className="flex items-center space-x-3 text-gray-700 bg-green-50 rounded-lg p-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <Zap className="h-4 w-4 text-green-600" />
                    </div>
                    <span className="text-sm">Smart comment moderation</span>
                  </div>
                </motion.div>
              </motion.div>
            </CardHeader>

            <CardContent className="px-8 pb-12">
              <motion.form
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                {step === "form" && (
                  <>
                    {/* Name Field */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="name"
                        className="text-sm font-medium text-gray-700"
                      >
                        Full Name *
                      </Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          id="name"
                          type="text"
                          placeholder="Enter your full name"
                          value={formData.name}
                          onChange={(e) =>
                            handleInputChange("name", e.target.value)
                          }
                          className={`pl-10 ${
                            errors.name
                              ? "border-red-500 focus:border-red-500"
                              : ""
                          }`}
                          disabled={isSubmitting}
                        />
                      </div>
                      {errors.name && (
                        <p className="text-sm text-red-600 flex items-center space-x-1">
                          <AlertCircle className="h-3 w-3" />
                          <span>{errors.name}</span>
                        </p>
                      )}
                    </div>

                    {/* Email Field */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="email"
                        className="text-sm font-medium text-gray-700"
                      >
                        Email Address *
                      </Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="Enter your email address"
                          value={formData.email}
                          onChange={(e) =>
                            handleInputChange("email", e.target.value)
                          }
                          className={`pl-10 ${
                            errors.email
                              ? "border-red-500 focus:border-red-500"
                              : ""
                          }`}
                          disabled={isSubmitting}
                        />
                      </div>
                      {errors.email && (
                        <p className="text-sm text-red-600 flex items-center space-x-1">
                          <AlertCircle className="h-3 w-3" />
                          <span>{errors.email}</span>
                        </p>
                      )}
                    </div>

                    {/* Phone Field */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="phone"
                        className="text-sm font-medium text-gray-700"
                      >
                        Phone Number *
                      </Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          id="phone"
                          type="tel"
                          placeholder="+216 XX XXX XXX"
                          value={formData.phone}
                          onChange={(e) =>
                            handleInputChange("phone", e.target.value)
                          }
                          className={`pl-10 ${
                            errors.phone
                              ? "border-red-500 focus:border-red-500"
                              : ""
                          }`}
                          disabled={isSubmitting}
                        />
                      </div>
                      {errors.phone && (
                        <p className="text-sm text-red-600 flex items-center space-x-1">
                          <AlertCircle className="h-3 w-3" />
                          <span>{errors.phone}</span>
                        </p>
                      )}
                      <p className="text-xs text-gray-500">
                        Include country code (e.g., +1 for US, +33 for France)
                      </p>
                    </div>
                  </>
                )}

                {step === "code" && (
                  <>
                    <div className="space-y-2">
                      <Label
                        htmlFor="code"
                        className="text-sm font-medium text-gray-700"
                      >
                        Enter the 6-digit code sent to {formData.email}
                      </Label>
                      <div className="relative">
                        <Input
                          id="code"
                          type="text"
                          inputMode="numeric"
                          placeholder="123456"
                          value={verificationCode}
                          onChange={(e) =>
                            setVerificationCode(
                              e.target.value.replace(/[^\d]/g, "").slice(0, 6)
                            )
                          }
                          className="pl-3 tracking-widest text-center"
                          disabled={isSubmitting}
                        />
                      </div>
                      {codeStatus && (
                        <p className="text-sm text-red-600 flex items-center space-x-1">
                          <AlertCircle className="h-3 w-3" />
                          <span>{codeStatus}</span>
                        </p>
                      )}
                      <div className="text-sm text-gray-600 flex items-center justify-between">
                        <button
                          type="button"
                          onClick={() => setStep("form")}
                          className="underline"
                        >
                          Change email
                        </button>
                        <button
                          type="button"
                          onClick={handleResendCode}
                          className="underline"
                          disabled={resendCooldown > 0 || isSubmitting}
                        >
                          {resendCooldown > 0
                            ? `Resend code in ${resendCooldown}s`
                            : "Resend code"}
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {/* Submit Error */}
                {submitError && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <div className="flex items-center space-x-2">
                      <AlertCircle className="h-4 w-4 text-red-600" />
                      <p className="text-sm text-red-600">{submitError}</p>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1 }}
                >
                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-6 text-lg font-semibold shadow-lg transition-all duration-200 hover:shadow-xl transform hover:scale-[1.02]"
                    size="lg"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <div className="flex items-center space-x-2">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span>
                          {step === "form" ? "Sending Code..." : "Verifying..."}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="h-5 w-5" />
                        <span>
                          {step === "form"
                            ? "Send Verification Code"
                            : "Verify & Complete Setup"}
                        </span>
                      </div>
                    )}
                  </Button>
                </motion.div>

                {/* Logout Option */}
                <div className="text-center pt-4">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleLogout}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    Sign in with a different account
                  </Button>
                </div>
              </motion.form>
            </CardContent>
          </Card>

          {/* Privacy Notice */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="mt-6 text-center"
          >
            <p className="text-xs text-gray-500">
              By completing signup, you agree to our{" "}
              <a href="/terms" className="text-blue-600 hover:underline">
                Terms of Service
              </a>{" "}
              and{" "}
              <a href="/privacy" className="text-blue-600 hover:underline">
                Privacy Policy
              </a>
              .
            </p>
          </motion.div>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="text-center mt-8"
          >
            <p className="text-gray-600 text-sm">
              Made for Tunisian businesses 🇹🇳
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default SignupPage;
